import type { DatePickerLocale } from './types';

/**
 * Every IANA zone inside the United States and its territories, including the legacy aliases a
 * browser can still report. `US/*` aliases are matched by prefix instead of listed.
 *
 * Matched exactly rather than by an `America/` prefix, because Canada and Latin America sit under
 * `America/` too and write their dates day-first.
 */
const US_TIME_ZONES = new Set([
  'America/Adak',
  'America/Anchorage',
  'America/Boise',
  'America/Chicago',
  'America/Denver',
  'America/Detroit',
  'America/Fort_Wayne',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indianapolis',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Knox_IN',
  'America/Los_Angeles',
  'America/Louisville',
  'America/Menominee',
  'America/Metlakatla',
  'America/New_York',
  'America/Nome',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Phoenix',
  'America/Puerto_Rico',
  'America/Shiprock',
  'America/Sitka',
  'America/Yakutat',
  'Navajo',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Pago_Pago',
  'Pacific/Saipan',
]);

/**
 * The reading order a visitor most likely expects, taken from the browser's time zone.
 *
 * The time zone rather than `navigator.language`, because a UK machine very often reports
 * `en-US` — it is the default on a fresh install — while almost none is set to a US zone. This is
 * a stopgap until the app knows a community's region; a caller that does know passes `locale`.
 */
export const browserDatePickerLocale = (): DatePickerLocale => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return zone.startsWith('US/') || US_TIME_ZONES.has(zone) ? 'en-US' : 'en-GB';
  } catch {
    return 'en-GB';
  }
};
